import {
	AfterViewInit,
	Component,
	ElementRef,
	NgZone,
	OnDestroy,
	ViewChild,
} from '@angular/core';

interface GridTheme {
	lineRest: string;
	lineNearRgb: string;
	lineNearAlpha: (n: number) => number;
	dotRest: [number, number, number];
	dotNear: [number, number, number];
}

/**
 * Animated magnetic-grid background for the login page.
 * Canvas 2D + requestAnimationFrame logic ported verbatim from the lms-plus
 * prototype (public/home.component.ts, ~lines 56–333). All prototype framework
 * deps (ThemeService / BookingService / SessionService / portal picker) were
 * stripped — this component has NO data dependencies. Angular-13 class component
 * (no signals / no standalone). Colours come from the scoped --lms-* tokens
 * (@include lms.brand-tokens in the scss), read once at init.
 */
@Component({
	selector: 'app-login-grid-bg',
	template: `<canvas #gridCanvas class="grid-canvas"></canvas>`,
	styleUrls: ['./login-grid-bg.component.scss'],
})
export class LoginGridBgComponent implements AfterViewInit, OnDestroy {
	@ViewChild('gridCanvas', { static: true })
	private gridCanvas!: ElementRef<HTMLCanvasElement>;

	// ── Magnetic-grid tuning (verbatim from the prototype) ──
	private static readonly SP = 44; // grid spacing (px)
	private static readonly R = 172; // radius of magnetic influence (px)
	private static readonly MAX_SHIFT = 30; // max node displacement at the focal point
	private static readonly EASE = 0.09; // focal lerp factor

	private ctx: CanvasRenderingContext2D | null = null;
	private w = 0;
	private h = 0;

	private cols = 0;
	private rows = 0;
	private baseX: number[] = [];
	private baseY: number[] = [];
	private dispX = new Float32Array(0);
	private dispY = new Float32Array(0);
	private prox = new Float32Array(0);

	private fx = 0;
	private fy = 0;
	private tx = 0;
	private ty = 0;

	private pointerActive = false;
	private lastMoveTs = 0;
	private driftT = 0;

	private rafId: number | null = null;
	private running = false;
	private reduced = false;
	private io: IntersectionObserver | null = null;
	private theme!: GridTheme;

	constructor(
		private readonly el: ElementRef<HTMLElement>,
		private readonly zone: NgZone,
	) {}

	private readonly onMove = (e: PointerEvent): void => {
		const rect = this.gridCanvas.nativeElement.getBoundingClientRect();
		this.tx = e.clientX - rect.left;
		this.ty = e.clientY - rect.top;
		this.pointerActive = true;
		this.lastMoveTs = performance.now();
	};
	private readonly onLeave = (): void => {
		this.pointerActive = false;
	};
	private readonly onResize = (): void => {
		this.size();
		if (this.reduced) this.drawStatic();
	};

	ngAfterViewInit(): void {
		const canvas = this.gridCanvas.nativeElement;
		this.ctx = canvas.getContext('2d');
		if (!this.ctx) return;

		this.theme = this.readTheme();
		this.reduced =
			!!window.matchMedia &&
			window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		this.size();
		this.fx = this.tx = this.w / 2;
		this.fy = this.ty = this.h / 2;

		window.addEventListener('resize', this.onResize);

		if (this.reduced) {
			this.drawStatic(); // one static frame — no loop, no pointer tracking
			return;
		}

		// Run the loop + listeners OUTSIDE Angular so raf never triggers change detection.
		this.zone.runOutsideAngular(() => {
			document.addEventListener('pointermove', this.onMove);
			document.documentElement.addEventListener('pointerleave', this.onLeave);

			this.io = new IntersectionObserver(
				(entries) => {
					const visible = entries[0]?.isIntersecting ?? true;
					if (visible) this.start();
					else this.stop();
				},
				{ threshold: 0 },
			);
			this.io.observe(this.el.nativeElement);

			this.start();
		});
	}

	ngOnDestroy(): void {
		this.stop(); // cancels requestAnimationFrame — no leak on navigation away
		window.removeEventListener('resize', this.onResize);
		document.removeEventListener('pointermove', this.onMove);
		document.documentElement.removeEventListener('pointerleave', this.onLeave);
		this.io?.disconnect();
		this.io = null;
	}

	// ── Colours from the scoped --lms-* tokens (read once) ──
	private readTheme(): GridTheme {
		const cs = getComputedStyle(this.el.nativeElement);
		const rest = this.rgb(cs.getPropertyValue('--lms-muted'), [107, 114, 128]);
		const near = this.rgb(cs.getPropertyValue('--lms-accent-light'), [34, 197, 94]);
		return {
			lineRest: `rgba(${rest[0]},${rest[1]},${rest[2]},0.16)`,
			lineNearRgb: `${near[0]},${near[1]},${near[2]}`,
			lineNearAlpha: (n) => 0.14 + n * 0.55,
			dotRest: rest,
			dotNear: near,
		};
	}

	/** Parse #rgb / #rrggbb token value → [r,g,b]; fall back if unset/malformed. */
	private rgb(
		val: string,
		fallback: [number, number, number],
	): [number, number, number] {
		let s = (val || '').trim().replace('#', '');
		if (s.length === 3) s = s.split('').map((c) => c + c).join('');
		if (s.length < 6 || /[^0-9a-fA-F]/.test(s.slice(0, 6))) return fallback;
		return [
			parseInt(s.slice(0, 2), 16),
			parseInt(s.slice(2, 4), 16),
			parseInt(s.slice(4, 6), 16),
		];
	}

	// ── Canvas sizing / point lattice (verbatim) ──
	private size(): void {
		const canvas = this.gridCanvas.nativeElement;
		const rect = this.el.nativeElement.getBoundingClientRect();
		this.w = rect.width;
		this.h = rect.height;
		const dpr = Math.min(window.devicePixelRatio || 1, 2);
		canvas.width = Math.round(this.w * dpr);
		canvas.height = Math.round(this.h * dpr);
		this.ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
		this.buildPoints();
	}

	private buildPoints(): void {
		const sp = LoginGridBgComponent.SP;
		const xs: number[] = [];
		const ys: number[] = [];
		for (let x = sp / 2; x < this.w; x += sp) xs.push(x);
		for (let y = sp / 2; y < this.h; y += sp) ys.push(y);
		this.cols = xs.length;
		this.rows = ys.length;
		const n = this.cols * this.rows;
		this.baseX = new Array(n);
		this.baseY = new Array(n);
		let i = 0;
		for (const y of ys) {
			for (const x of xs) {
				this.baseX[i] = x;
				this.baseY[i] = y;
				i++;
			}
		}
		this.dispX = new Float32Array(n);
		this.dispY = new Float32Array(n);
		this.prox = new Float32Array(n);
	}

	// ── Animation loop (verbatim) ──
	private start(): void {
		if (this.running || this.reduced) return;
		this.running = true;
		this.lastMoveTs = performance.now();
		this.loop();
	}

	private stop(): void {
		this.running = false;
		if (this.rafId !== null) {
			cancelAnimationFrame(this.rafId);
			this.rafId = null;
		}
	}

	private loop(): void {
		this.rafId = requestAnimationFrame(() => {
			this.step();
			if (this.running) this.loop();
		});
	}

	private step(): void {
		if (this.pointerActive && performance.now() - this.lastMoveTs > 2000) {
			this.pointerActive = false;
		}
		if (!this.pointerActive) {
			this.driftT += 0.005;
			this.tx = this.w / 2 + Math.cos(this.driftT * 0.7) * this.w * 0.18;
			this.ty = this.h / 2 + Math.sin(this.driftT * 0.9) * this.h * 0.16;
		}
		this.fx += (this.tx - this.fx) * LoginGridBgComponent.EASE;
		this.fy += (this.ty - this.fy) * LoginGridBgComponent.EASE;
		this.render();
	}

	// ── Rendering (verbatim math) ──
	private computeNodes(): void {
		const R = LoginGridBgComponent.R;
		const n = this.baseX.length;
		for (let i = 0; i < n; i++) {
			const dx = this.fx - this.baseX[i];
			const dy = this.fy - this.baseY[i];
			const d = Math.hypot(dx, dy);
			let prox = 0;
			let px = this.baseX[i];
			let py = this.baseY[i];
			if (d > 0 && d < R) {
				prox = 1 - d / R;
				const s = LoginGridBgComponent.MAX_SHIFT * prox;
				px += (dx / d) * s;
				py += (dy / d) * s;
			}
			this.dispX[i] = px;
			this.dispY[i] = py;
			this.prox[i] = prox;
		}
	}

	private render(): void {
		const ctx = this.ctx;
		if (!ctx) return;
		this.computeNodes();
		ctx.clearRect(0, 0, this.w, this.h);

		const t = this.theme;
		const cols = this.cols;
		const rows = this.rows;
		const dx = this.dispX;
		const dy = this.dispY;
		const pr = this.prox;

		const seg = (a: number, b: number): void => {
			const nn = Math.max(pr[a], pr[b]);
			if (nn > 0.001) {
				ctx.strokeStyle = `rgba(${t.lineNearRgb},${t.lineNearAlpha(nn).toFixed(3)})`;
				ctx.lineWidth = 1 + nn * 0.2;
			} else {
				ctx.strokeStyle = t.lineRest;
				ctx.lineWidth = 1;
			}
			ctx.beginPath();
			ctx.moveTo(dx[a], dy[a]);
			ctx.lineTo(dx[b], dy[b]);
			ctx.stroke();
		};
		for (let r = 0; r < rows; r++) {
			for (let c = 0; c < cols; c++) {
				const i = r * cols + c;
				if (c + 1 < cols) seg(i, i + 1);
				if (r + 1 < rows) seg(i, i + cols);
			}
		}

		const [r0, g0, b0] = t.dotRest;
		const [r1, g1, b1] = t.dotNear;
		for (let i = 0; i < dx.length; i++) {
			const n = pr[i];
			const cr = Math.round(r0 + (r1 - r0) * n);
			const cg = Math.round(g0 + (g1 - g0) * n);
			const cb = Math.round(b0 + (b1 - b0) * n);
			ctx.fillStyle = `rgb(${cr}, ${cg}, ${cb})`;
			ctx.beginPath();
			ctx.arc(dx[i], dy[i], 1.3 + n * 2.3, 0, Math.PI * 2);
			ctx.fill();
		}
	}

	/** A single resting frame (focal parked far away → no displacement). */
	private drawStatic(): void {
		this.fx = -100000;
		this.fy = -100000;
		this.render();
	}
}
